<template>
  <section class="master">
    <section class="dialog">
      <section class="pdf-container">
        <!-- <embed :src="pdfUrl" type="application/pdf" width="100%" height="600px" /> -->

        <div
          v-if="pdfError"
          class="pdf-error text-white flex column items-center justify-center"
        >
          <!-- <img :src="require('@/assets/pdf_icon-error.svg')" alt="PDF" /> -->
          <img
            class="w-20"
            :src="require('@/assets/img/pdf_icon.png')"
            alt="PDF"
          />
          <span class="text-[1.5em]">{{ pdfError }}</span>
        </div>

        <div v-else-if="usaPdfJs && pdfPages" ref="paginas" class="pdf-paginas">
          <canvas v-for="n in pdfPages" :key="n" :ref="'pagina' + n" :style="{ width: zoom * 100 + '%' }" />
        </div>

        <embed
          v-else-if="pdfBlobUrl && !usaPdfJs"
          :key="pdfBlobUrl"
          :src="pdfBlobUrl"
          type="application/pdf"
          width="100%"
          height="600px"
        />

        <div v-else class="text-white flex items-center justify-center">
          <span class="text-[1.2em]">A carregar PDF...</span>
        </div>
      </section>
      <section class="actions">
        <AppButton
          v-bind:visibility="true"
          left_icon="fa-regular fa-circle-xmark fa-xl"
          emitType="close"
          @close="$emit('close')"
          :width="20"
          :height="20"
          :padding="'0px'"
        />

        <template v-if="usaPdfJs && pdfPages">
          <button @click="mudarZoom(0.5)" aria-label="Aumentar">+</button>
          <button @click="mudarZoom(-0.5)" aria-label="Diminuir">−</button>
        </template>

        <div class="space"></div>

        <button v-if="emailSend" @click="sendEmail">
          <Icon icon="mdi:email-send" width="24" height="24" />
        </button>
      </section>
      <!-- </v-card> -->
    </section>
  </section>
</template>

<script>
//    import { mapGetters } from 'vuex';
import AppButton from "@/components/updated/button.vue";
import { markRaw } from "vue";
import axios from "axios";
import { isNativeApp } from "@/native/tracker";

// Onde o <embed> de PDF não funciona e é preciso desenhar com o pdf.js:
// - app Android (o WebView não tem leitor de PDF);
// - browsers sem leitor de PDF embebido (navigator.pdfViewerEnabled = false,
//   ex.: Chrome no Android, também nos tablets);
// - ecrãs táteis (tablets e telemóveis): o Safari do iPad só mostra a
//   primeira página de um PDF embebido.
function semLeitorPdf() {
  if (isNativeApp()) return true;
  if (navigator.pdfViewerEnabled === false) return true;
  return window.matchMedia?.("(pointer: coarse)").matches ?? false;
}
export default {
  components: {
    AppButton,
  },
  props: {
    pdfUrl: {
      type: String,
      required: true,
      default: "",
    },

    emailSend: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      pdfBlobUrl: "",
      pdfRawBlobUrl: "", // sem "#zoom=..." — é este que tem de ser revogado
      pdfError: "",
      dialog: false,
      pdfBlob: null,
      usaPdfJs: semLeitorPdf(),
      pdfPages: 0,
      zoom: 1,
      pdfDoc: null,
      // pdfUrl: ''
    };
  },
  methods: {
    sendEmail() {
      if (this.pdfBlob) {
        this.$emit("send-email", {
          url: this.pdfUrl,
          blob: this.pdfBlob,
        });
      } else {
        console.error("PDF Blob not available");
      }
    },

    // Sem leitor de PDF embebido (ver semLeitorPdf) o <embed> fica em branco,
    // por isso desenhamos as páginas em <canvas> com o pdf.js.
    async renderNativo(blob) {
      const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
      const worker = await import("pdfjs-dist/legacy/build/pdf.worker.entry");
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

      // Fora do data(): o Proxy reativo do Vue parte os campos privados (#)
      // do pdf.js ("Private element is not present on this object").
      const doc = await pdfjs.getDocument({ data: await blob.arrayBuffer() }).promise;
      this.pdfDoc = markRaw(doc);
      this.pdfPages = doc.numPages;
      await this.$nextTick();
      await this.desenharPaginas();
    },

    async mudarZoom(delta) {
      const novo = Math.min(4, Math.max(1, this.zoom + delta));
      if (novo === this.zoom) return;
      this.zoom = novo;
      await this.$nextTick();
      await this.desenharPaginas();
    },

    async desenharPaginas() {
      const doc = this.pdfDoc;
      const token = (this.renderToken = (this.renderToken || 0) + 1);
      const largura = this.$refs.paginas.clientWidth * this.zoom;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      for (let n = 1; n <= this.pdfPages; n++) {
        if (token !== this.renderToken) return; // novo zoom pedido entretanto
        const page = await doc.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: (largura / base.width) * dpr });
        const canvas = this.$refs["pagina" + n][0];
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      }
    },

    onAppBack(e) {
      e.preventDefault();
      this.$emit("close");
    },

    async fetchPdf() {
      try {
        // Faz a requisição para buscar o PDF via axios com headers (incluindo X-Schema definido globalmente)
        const response = await axios.get(this.pdfUrl, {
          responseType: "blob",
        });

        if (response.status === 208) {
          this.pdfError = "PDF não encontrado.";
          return;
        }

        // Store the blob for later use
        this.pdfBlob = response.data;

        // Cria uma URL temporária para o blob.
        // O fragmento "#zoom=page-width" força o leitor de PDF nativo do
        // browser a ajustar a página à largura do <embed> — sem isto, o
        // zoom "automático" nem sempre preenche a largura (fica com
        // margens), sobretudo em páginas A4 landscape. Mantemos a URL sem
        // fragmento à parte porque URL.revokeObjectURL() só reconhece a
        // blob URL exata.
        this.pdfRawBlobUrl = URL.createObjectURL(response.data);
        this.pdfBlobUrl = `${this.pdfRawBlobUrl}#zoom=page-width`;

        if (this.usaPdfJs) await this.renderNativo(response.data);
      } catch (error) {
        console.error("Erro ao buscar PDF:", error);
        if (error.response) {
          // Caso haja uma resposta do servidor
          if (error.response.data && error.response.data.error) {
            if (error.response.data.error === "Missing X-Schema header") {
              this.pdfError = "PDF não encontrado.";
            } else {
              this.pdfError = `Erro ao carregar o PDF: ${error.response.data.error}`;
            }
          } else if (error.response.status === 404) {
            this.pdfError = "PDF não encontrado. (404)";
          } else {
            this.pdfError = `Erro ao carregar o PDF (status: ${error.response.status}).`;
          }
        } else if (error.message) {
          this.pdfError = `Erro: ${error.message}`;
        } else {
          this.pdfError = "Erro desconhecido ao carregar o PDF.";
        }
      }
    },
  },
  async created() {
    await this.fetchPdf();
  },
  mounted() {
    window.addEventListener("app-back", this.onAppBack);
  },
  beforeUnmount() {
    window.removeEventListener("app-back", this.onAppBack);
    if (this.pdfDoc) this.pdfDoc.destroy();
    // Clean up blob URL to prevent memory leaks
    if (this.pdfRawBlobUrl) {
      URL.revokeObjectURL(this.pdfRawBlobUrl);
    }
  },
};
</script>

<style scoped>
.master {
  padding: 2em;
  background-color: var(--bg-pop-up);
  background-color: #0005;
  backdrop-filter: blur(5px);
  border-radius: 1em;
  /*width: 100%; */
  width: 100vw;
  height: 100dvh;
  position: fixed;
  display: flex;
  justify-content: center;
  top: 0;
  left: 0;

  z-index: 10000;
}

.dialog {
  width: 100%;
  height: 100%;
  display: flex;
  backdrop-filter: brightness(0.5);
}

.pdf-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  /* background-color: #fff; */
}

/* O <embed> de PDF é retirado do fluxo flex e fixado por posicionamento
   absoluto: como elemento "replaced", o Firefox calcula o seu min-width
   flex a partir do tamanho intrínseco do próprio plugin em vez de respeitar
   width:100%, o que fazia o PDF renderizar numa faixa estreita em vez de
   preencher o container. Absolute + inset:0 ignora esse cálculo de todo. */
.pdf-container embed {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.pdf-paginas {
  position: absolute;
  inset: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  background: #555;
}

.actions {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1em;
  padding: 1em;
  background-color: #0004;
  background-color: var(--secundary);
  background-color: var(--primary_1);
}

.actions button {
  padding: 0.2em;
  background-color: var(--amarelo);
  border: none;
  border-radius: 0.3em;
  color: #fff;
  font-size: 1em;
  cursor: pointer;
}
/* Ecrãs estreitos (telemóvel/tablet na vertical): PDF a toda a largura,
   sem a margem de 2em, e os botões passam a barra fina no topo. */
@media (max-width: 800px) {
  .master {
    padding: 0;
    border-radius: 0;
  }
  .dialog {
    flex-direction: column-reverse;
  }
  .actions {
    flex-direction: row;
    align-items: center;
    padding: 0.4em 0.8em;
    gap: 0.6em;
  }
  .actions .space {
    flex: 1;
  }
}
</style>
